import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useDeferredValue, useMemo, useState } from "react";
import { PageContainer } from "../components/core/basic/PageContainer";
import BreadcrumbDates, { type Breadcrumb } from "../components/core/blocks/BreadcrumbDates";
import MetricsCard from "../components/custom/merchandise/MetricsCard";
import { usePersistedTableState } from "../components/table/usePersistedTableState";
import { idsToVisibilityState, selectionStateToSelectedIds } from "../components/table/utils";
import { DatesProvider, useDates } from "../dates/DatesContext";
import useCategories from "../hooks/useCategories";
import { useAppSelector } from "../redux/hooks";
import { useModuleEnabled } from "../redux/selectors/modules";
import { useAcosOrRoas } from "../utils/acosOrRoas";
import { DASHBOARD_LINKS, AGENCY_DASHBOARD_BREADCRUMB_SPEC } from "../utils/dashboardLinks";
import { buildMetric } from "../views/merchandise/metrics";
import { COMPARE_METRIC_LOOKUP } from "../views/merchandise/tables/metricsByEntity";
import { useGetV4AdvReportByDate } from "../modules/perf_reports/api/v4AdvReportByDate";
import { useGetV4AdvReportTotals } from "../modules/perf_reports/api/v4AdvReportTotals";
import AdTypeSection from "./advertising/adTypeSection/AdTypeSection";
import { useParams } from "react-router";
import Accordion from "components/core/basic/Accordion";
import { AdReportTable, getAdTypeFilterOptions } from "modules/perf_reports/ad_report/AdReportTable";
import useAdReportColumns from "modules/perf_reports/ad_report/useAdReportColumns";
import AdPlanModal from "components/core/blocks/AdPlan/AdPlanModal";
import AdPlanBulkCreate from "components/core/blocks/AdPlan/AdPlanBulkCreate";
import { HStack } from "../../styled-system/jsx";
import AnnotationsTable from "components/custom/growth/AnnotationsTable";
import { useFetch } from "hooks/api";
import { useStandardEntityMetrics } from "./merchandise/tables/metricUtils";
import { Redirect } from "react-router-dom";
import DashboardSelector from "./advertising/DashboardSelector";
import { getCategoryReportOptions } from "../modules/categories/api/categoryReport";
import QueryFunnel from "modules/query_funnel/QueryFunnel";

interface Props {
  categoryId?: string;
  title: string;
}

export interface AdType {
  value: string | number;
  label: string;
}

function AdvertisingReportBody({ categoryId, title }: Props) {
  const acosOrRoas = useAcosOrRoas();
  const [adType, setAdType] = useState<string | number | null>("all");
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const { start, end, group, preStart, preEnd } = useDates();
  const startDate = start.format("YYYY-MM-DD");
  const endDate = end.format("YYYY-MM-DD");
  const [quickFilterValue, setQuickFilterValue] = useState("all");
  const mediaPlanMetrics = useStandardEntityMetrics("mediaPlan");
  const categoryMetrics = useStandardEntityMetrics("category");
  const columns = useAdReportColumns();
  const defaultSelectedColumns = [
    "state",
    "created_datetime",
    "ad_sales",
    "ad_spend",
    "acos",
    "roas",
    "orders",
    "impressions",
    "clicks",
    "ctr",
    "conversion_rate",
    "cpc",
  ];
  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id!),
  );
  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "adReport",
    initialState: {
      columnVisibility: {
        ...defaultHiddenCols,
        acos: acosOrRoas(true, false),
        roas: acosOrRoas(false, true),
      },
      columnOrder: defaultSelectedColumns,
      columnPinning: {
        left: ["ad_name"],
      },
      sorting: [{ id: "ad_sales", desc: true }],
    },
  });
  const deferredSearch = useDeferredValue(state.globalFilter);
  const selectedIds = selectionStateToSelectedIds(state.rowSelection);
  const categoryFilters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start_date: startDate,
    end_date: endDate,
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    channel: channel,
    empty: true,
    group_by: "category_id",
    category: categoryId,
  };

  const { data: categoryData } = useQuery(getCategoryReportOptions(categoryFilters));

  const { categories } = useCategories(categoryId, "advertising/dashboardv2");
  const adTypeFilterOptions = getAdTypeFilterOptions(channel);

  const filters = useMemo(() => {
    const selectedIdsFilter =
      selectedIds.length > 0 ? [{ id: "ad_id", value: { op: "anyof", value: selectedIds } }] : [];
    const adTypeFilter = adType !== "all" ? [{ id: "ad_type", value: { op: "=", value: adType } }] : [];
    const categoryFilter = categoryId ? [{ id: "category_id", value: { op: "=", value: categoryId } }] : [];
    const quickFilter = quickFilterValue !== "all" ? [{ id: quickFilterValue, value: { op: "=", value: true } }] : [];
    return [...state.columnFilters, ...selectedIdsFilter, ...adTypeFilter, ...categoryFilter, ...quickFilter];
  }, [state.columnFilters, selectedIds, adType, categoryId, quickFilterValue]);

  const byDateOptionsGetter = useGetV4AdvReportByDate();
  const advByDateReportQuery = useQuery(
    byDateOptionsGetter({
      filters,
      search: deferredSearch,
    }),
  );
  const totalsOptionsGetter = useGetV4AdvReportTotals();
  const advTotalsReportQuery = useQuery(
    totalsOptionsGetter({
      filters,
      search: deferredSearch,
    }),
  );

  const showTotalSales = useMemo(() => {
    return !(
      deferredSearch ||
      selectedIds.length ||
      adType !== "all" ||
      state.columnFilters.length ||
      quickFilterValue !== "all"
    );
  }, [deferredSearch, selectedIds.length, adType, state.columnFilters.length, quickFilterValue]);

  const metrics = useMemo(() => {
    const data = {
      total: advTotalsReportQuery.data?.data,
    };
    // If we we're not filtering, searching, or selecting, we can include that category level data
    if (showTotalSales) {
      if (data.total && categoryData?.total) {
        data.total = {
          ...data.total,
          ...categoryData.total,
        };
      }
    } else {
      data.total = advTotalsReportQuery.data?.data;
    }
    const metrics = showTotalSales ? categoryMetrics : mediaPlanMetrics;
    return metrics.map((id) => buildMetric(marketPlace, data, id, COMPARE_METRIC_LOOKUP[id]));
  }, [advTotalsReportQuery.data?.data, marketPlace, categoryData, showTotalSales, mediaPlanMetrics, categoryMetrics]);

  const timeLineData = useMemo(() => {
    if (showTotalSales) {
      return categoryData?.date.map((row) => ({ ...row, name: row.start_date }));
    }
    return advByDateReportQuery.data?.data.map((row) => ({ ...row, name: row.start_date }));
  }, [advByDateReportQuery.data?.data, categoryData?.date, showTotalSales]);

  const { data: annotationData, isLoading: isAnnotationDataLoading } = useFetch<{ data: unknown[] }>(
    ["annotations", start, end, channel, categoryId, marketPlace.marketPlace],
    "/api/annotations/",
    {
      groupby: group,
      ts_after: `${startDate}T00:00:00`,
      ts_before: `${endDate}T23:59:59`,
      category: categoryId,
      marketplace: marketPlace.marketPlace,
      channel: channel === "amazon" ? "AMZ" : channel,
    },
    // ts_before and ts_after need values from categories to contain start and end date within query string
    {
      placeholderData: keepPreviousData,
      enabled: !!categoryId,
    },
  );

  const annotationDataList = annotationData?.data ?? [];
  const isStaff = useAppSelector((state) => state.user.is_staff);
  const debugModule = useModuleEnabled("DEBUG");
  const canViewDebug = isStaff && debugModule;
  return (
    <PageContainer withBreadcrumb>
      <MetricsCard
        id={showTotalSales ? "adversitingOverview" : "adversitingOverviewWithFilters"}
        title={title}
        data={timeLineData || []}
        loading={
          advByDateReportQuery.isFetching && (!advByDateReportQuery.data || advByDateReportQuery.isPlaceholderData)
        }
        showLoadingSpinner
        showSkeletonLoadingEffect={
          advTotalsReportQuery.isFetching && (!advTotalsReportQuery.data || advTotalsReportQuery.isPlaceholderData)
        }
        metrics={metrics}
        showMetricsSelector
        initialSelectedMetric={["ad_sales", "ad_spend", acosOrRoas("acos", "roas")]}
        group={group}
        showAnnotations={true}
        byline={
          <>
            {start.format("MMMM Do YYYY")}
            {" - "}
            {end.format("MMMM Do YYYY")}
          </>
        }
        context={"adReport"}
      >
        <HStack justifyContent={"end"}>
          {/*  eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <AdPlanModal dashboard={false} categories={categories} categoryId={categoryId} {...({} as any)} />
          {/*  eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <AdPlanBulkCreate categoryId={categoryId} marketPlace={marketPlace} channel={channel} {...({} as any)} />
        </HStack>
        <Accordion title={"Ad Types"}>
          <AdTypeSection
            adTypeFilter={adTypeFilterOptions.find((o: AdType) => o.value === adType)}
            setAdTypeFilter={(adType: AdType) => {
              setAdType(adType.value);
            }}
            additionalFilters={filters}
            search={deferredSearch}
          />
        </Accordion>
        <Accordion title={"Media Plans"} initialExpanded>
          <AdReportTable
            state={state}
            initialState={initialState}
            setters={setters}
            quickFilterValue={quickFilterValue}
            setQuickFilterValue={setQuickFilterValue}
            adType={adType}
            setAdType={setAdType}
            additionalFilters={{
              categoryId,
              adType: String(adType),
            }}
            columns={columns}
          />
        </Accordion>
        {canViewDebug && <QueryFunnel category={categoryId!} />}

        <Accordion title={"Impact Analysis"}>
          <AnnotationsTable
            data={annotationDataList}
            isLoading={isAnnotationDataLoading}
            marketPlace={marketPlace.marketPlace}
            annotationType={"advertisement"}
          />
        </Accordion>
      </MetricsCard>
    </PageContainer>
  );
}
interface Params {
  id?: string;
}

export default function AdvertisingDashboardV2() {
  const hasAgencyDashboardModule = useModuleEnabled("AGENCY_DASHBOARD");
  const hasAdsV1Module = useModuleEnabled("ADS_V1");

  const { id: categoryId } = useParams<Params>();

  const { currentCategoryDetails: categoryDetails, categories: categoryData } = useCategories(
    categoryId,
    "advertising/dashboardv2",
  );
  const categoryOptions = useMemo(
    () => [{ href: "/user/advertising/dashboardv2", name: "All Categories" }, ...(categoryData ?? [])],
    [categoryData],
  );
  const currentCategory = useMemo(() => {
    if (categoryId) {
      return {
        name: categoryDetails.leaf_category_name ?? "",
        href: `/user/advertising/dashboardv2/category/${categoryId}`,
      };
    }
    return {
      name: "All Categories",
      href: "/user/advertising/dashboardv2",
    };
  }, [categoryId, categoryDetails.leaf_category_name]);

  const breadCrumbItems: Breadcrumb[] = [
    {
      name: "Advertisement Dashboard",
      href: "/user/advertising/dashboard",
      dropdownLinkOptions: DASHBOARD_LINKS,
      searchable: false,
      dropdownSubtitle: "Reports",
    },
  ];

  breadCrumbItems.push({
    name: currentCategory.name,
    href: currentCategory.href,
    dropdownLinkOptions: categoryOptions,
    searchable: true,
    searchPlaceholderText: "Search categories...",
    dropdownSubtitle: "Categories",
  });

  if (hasAgencyDashboardModule) {
    breadCrumbItems.unshift(AGENCY_DASHBOARD_BREADCRUMB_SPEC);
  }

  if (hasAdsV1Module) {
    if (categoryId) {
      return <Redirect to={`/user/advertising/dashboard/category/${categoryId}`} />;
    } else {
      return <Redirect to={`/user/advertising/dashboard`} />;
    }
  }

  return (
    <DatesProvider>
      <DashboardSelector />
      <BreadcrumbDates items={breadCrumbItems} />
      <AdvertisingReportBody
        categoryId={categoryId}
        title={categoryId ? currentCategory.name : "Advertising Overview"}
      />
    </DatesProvider>
  );
}
